import type { RowNode } from "@ag-grid-community/core";
import {
  encodeStringToUtf8WithBom,
  initiateDownloadFromBytes,
  isNotNullOrUndefined,
} from "@/app/apiutils";
import {
  GridGroupValue,
  GridRowValue,
  documentGenerationService,
} from "@/app/services/generated/documentgenerationService";
import { EnumDocumentFileType } from "@/app/services/generated/enums/enumDocumentFileType.generated";
import { googleSheetLinkService } from "@/app/services/generated/googlesheetlinkService";
import Guid from "@/app/types/common/guid";
import { mainRoutes } from "@/app/ui/navigation/mainMenu";
import type {
  BmGridColumnConfiguration,
  BmGridColumnConfigurations,
  Row,
} from "@/components/controls/grid/BmGridData";
import {
  isContentRow,
  isRowGroup,
} from "@/components/controls/grid/BmGridData";
import { indexedKeyComparer } from "@/components/controls/grid/BmGridComparers";
import messages from "@/plugins/i18n";
import router from "@/router/router";
import userStore from "@/store/modules/userStore";

export type ExportColumnEntry = [string, BmGridColumnConfiguration];

export interface DocGenGridShape {
  title: string;
  columnTitles: string[];
  grid: readonly GridGroupValue[];
}

/**
 * Build action-free column entries for export. When ordering for AgGrid,
 * sort by the given column order.
 */
export function getExportColumnEntries(
  visibleColumns: BmGridColumnConfigurations,
  groupedColumns: BmGridColumnConfigurations,
  options?: {
    includeGroupedColumns?: boolean;
    columnOrder?: readonly string[];
    orderForAgGrid?: boolean;
  }
): ExportColumnEntry[] {
  const includeGrouped = options?.includeGroupedColumns ?? false;
  const columnEntries: ExportColumnEntry[] = [
    ...(includeGrouped ? Object.entries(groupedColumns) : []),
    ...Object.entries(visibleColumns),
  ].filter((col) => !col[1].rendererMetadata.actions) as ExportColumnEntry[];

  if (options?.orderForAgGrid && options.columnOrder) {
    columnEntries.sort(indexedKeyComparer(options.columnOrder, (x) => x[0]));
  }

  return columnEntries;
}

export function buildGridCsv(options: {
  columns: readonly BmGridColumnConfiguration[];
  items: readonly any[];
  delimiter?: string;
  stringMarker?: string;
}): string {
  const delimiter = options.delimiter ?? ",";
  const stringMarker = options.stringMarker ?? '"';
  const columns = options.columns;

  // escapes instances of `stringMarker` with {stringMarker}{stringMarker},
  // and then adds prefix and suffix.
  // i.e. `bob"bob` becomes `"bob""bob"` if stringMarker=`"`
  const stringFormatter = (s: string): string => {
    const matcher = RegExp("\\" + stringMarker, "g");
    const escaped = s.replace(matcher, stringMarker + stringMarker);
    return stringMarker + escaped + stringMarker;
  };

  const header = columns.map((col) => stringFormatter(col.headingText));

  const columnExtractors = columns.map(
    (col) => (item: any) =>
      col.csvformatter(col.extractor(item), item, stringFormatter)
  );
  const rowExtractor = (item: any) =>
    columnExtractors.map((a) => a(item)).join(delimiter);

  const lines = [header].concat(
    options.items.map((item) => rowExtractor(item))
  );
  return lines.join("\r\n");
}

function mapVirtualGridToGenerateDocApi(
  groupRow: GridGroupValue,
  rows: readonly Row[],
  visibleDataColumns: ExportColumnEntry[],
  groupedColumns: BmGridColumnConfigurations
) {
  // array of group rows
  if (rows[0] && isRowGroup(rows[0])) {
    groupRow.subGroups = rows.filter(isRowGroup).map((group) => {
      const rowGroup = new GridGroupValue();

      // First grouped column is special as it has to include the grouping name
      rowGroup.groupName = `${
        groupedColumns[group.columnKey].headingText
      }: ${group.columnValue}`;

      rowGroup.cells = visibleDataColumns.slice(1).map((column) => {
        const data = group.aggregateValues[column[0]];
        return data && isNotNullOrUndefined(data.value)
          ? column[1].formatter(data.value, undefined).toString()
          : "";
      });

      mapVirtualGridToGenerateDocApi(
        rowGroup,
        group.children,
        visibleDataColumns,
        groupedColumns
      );
      return rowGroup;
    });
  }

  // array of content rows
  if (rows[0] && isContentRow(rows[0])) {
    groupRow.rows = rows.filter(isContentRow).map((row) => {
      const gridValue = new GridRowValue();
      gridValue.cells = visibleDataColumns.map(
        (columnDef) => columnDef[1].extractAndFormat(row.item)?.toString() ?? ""
      );
      return gridValue;
    });
  }
}

function mapAgGridRowsToGenerateDocApi(
  groupRow: GridGroupValue,
  rows: readonly RowNode[],
  visibleDataColumns: ExportColumnEntry[]
) {
  if (rows[0] && rows[0].group) {
    groupRow.subGroups = rows
      .filter((row) => row.rowGroupIndex === rows[0].rowGroupIndex)
      .map((row) => {
        const rowGroup = new GridGroupValue();
        rowGroup.groupName = `${row.rowGroupColumn?.getUserProvidedColDef()?.headerName ?? ""}: ${row.key}`;

        rowGroup.cells = visibleDataColumns.slice(1).map((column) => {
          const value = row.aggData?.[column[0]];

          if (value === undefined) {
            return "";
          }

          return (column[1].formatter(value, undefined) || "").toString();
        });

        mapAgGridRowsToGenerateDocApi(
          rowGroup,
          row.childrenAfterSort ?? [],
          visibleDataColumns
        );

        return rowGroup;
      });

    groupRow.rows = rows
      .filter((row) => !row.group)
      .map((row) => {
        const gridValue = new GridRowValue();
        gridValue.cells = visibleDataColumns.map((columnDef) =>
          (columnDef[1].extractAndFormat(row.data) || "").toString()
        );
        return gridValue;
      });
  } else {
    groupRow.rows = rows.map((row) => {
      const gridValue = new GridRowValue();
      gridValue.cells = visibleDataColumns.map((columnDef) =>
        (columnDef[1].extractAndFormat(row.data) || "").toString()
      );
      return gridValue;
    });
  }
}

function finalizeDocGenShape(
  title: string,
  columnTitles: string[],
  parentGroupRow: GridGroupValue
): DocGenGridShape {
  return {
    title,
    columnTitles,
    grid:
      parentGroupRow.subGroups.length > 0
        ? parentGroupRow.subGroups
        : [parentGroupRow],
  };
}

export function buildDocGenGridFromVirtualRows(options: {
  title: string;
  visibleDataColumns: ExportColumnEntry[];
  groupedColumns: BmGridColumnConfigurations;
  rows: readonly Row[];
}): DocGenGridShape {
  const parentGroupRow = new GridGroupValue();
  mapVirtualGridToGenerateDocApi(
    parentGroupRow,
    options.rows,
    options.visibleDataColumns,
    options.groupedColumns
  );

  return finalizeDocGenShape(
    options.title,
    options.visibleDataColumns.map((c) => c[1].headingText),
    parentGroupRow
  );
}

export function buildDocGenGridFromAgGridRows(options: {
  title: string;
  visibleDataColumns: ExportColumnEntry[];
  agGridRows: readonly RowNode[];
}): DocGenGridShape {
  const parentGroupRow = new GridGroupValue();
  const agGridRows = options.agGridRows;
  mapAgGridRowsToGenerateDocApi(
    parentGroupRow,
    agGridRows.some((row) => row.group)
      ? agGridRows.filter((row) => row.rowGroupIndex === 0)
      : agGridRows,
    options.visibleDataColumns
  );

  return finalizeDocGenShape(
    options.title,
    options.visibleDataColumns.map((c) => c[1].headingText),
    parentGroupRow
  );
}

export async function generateGridPdf(
  shape: DocGenGridShape
): Promise<Uint8Array> {
  return await documentGenerationService.generateGridDocument(
    EnumDocumentFileType.pdf,
    shape.title,
    shape.columnTitles,
    shape.grid
  );
}

export async function openGeneratedGoogleSheet(
  shape: DocGenGridShape
): Promise<void> {
  const url = await documentGenerationService.generateGoogleSheetFile(
    shape.title,
    shape.columnTitles,
    shape.grid
  );
  window.open(url, "_blank");
}

/**
 * Ensure Google Sheets is linked for the current user, then open a sheet
 * for the given grid shape.
 */
export async function exportGridToGoogleSheets(
  shape: DocGenGridShape
): Promise<void> {
  const isActive = await googleSheetLinkService.isGoogleSheetLinkActive(
    userStore.state.user?.id ?? Guid.createEmpty()
  );

  if (!isActive) {
    const route = router.resolve(mainRoutes.googleSheetSuccess.location());
    const callbackUrl = new URL(route.href, window.location.origin);

    const oauthUrl = await googleSheetLinkService.commenceGoogleSheetLink(
      userStore.state.user?.id ?? Guid.createEmpty(),
      callbackUrl.href
    );

    const loginWindow = window.open(
      oauthUrl,
      messages.calendar_link_to_google,
      "width=500, height=650"
    );

    if (loginWindow) {
      loginWindow.opener.onSuccess = async () => {
        setTimeout(() => loginWindow.close(), 2000);
        await openGeneratedGoogleSheet(shape);
      };
    }
  } else {
    await openGeneratedGoogleSheet(shape);
  }
}

export function downloadGridCsv(
  filename: string,
  columns: readonly BmGridColumnConfiguration[],
  items: readonly any[]
): void {
  initiateDownloadFromBytes(filename, "text/csv;charset=utf-8", () =>
    Promise.resolve(
      encodeStringToUtf8WithBom(
        buildGridCsv({
          columns,
          items,
        })
      )
    )
  );
}

export function downloadGridPdf(
  filename: string,
  shape: DocGenGridShape
): void {
  initiateDownloadFromBytes(filename, "application/pdf", () =>
    Promise.resolve(generateGridPdf(shape))
  );
}
